import React, { useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Line,
  ComposedChart,
  Legend,
  Cell,
} from "recharts";
import {
  Droplets,
  Clock,
  IndianRupee,
  AlertOctagon,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Activity,
  Calendar,
  AlertTriangle,
  Zap,
  Settings,
  Download,
  Fuel,
} from "lucide-react";
import { RiWaterFlashLine } from "react-icons/ri";
import { useSelector } from "react-redux";
import { FaArtstation } from "react-icons/fa";

const hourlyData = [
  { time: "06 AM", actual: 12, avg: 10, status: "Normal" },
  { time: "08 AM", actual: 45, avg: 35, status: "Peak" },
  { time: "10 AM", actual: 55, avg: 40, status: "Peak" },
  { time: "12 PM", actual: 30, avg: 25, status: "Normal" },
  { time: "02 PM", actual: 20, avg: 22, status: "Idle" },
  { time: "04 PM", actual: 65, avg: 30, status: "Spike" },
  { time: "06 PM", actual: 50, avg: 45, status: "Normal" },
  { time: "08 PM", actual: 15, avg: 15, status: "Idle" },
];

const anomalyInsights = [
  {
    id: 1,
    station: "Station A-12",
    type: "spike",
    value: "+210% vs Avg",
    desc: "Sudden spike in dispensing detected at 04 PM. Potential unrecorded dispensing.",
    severity: "critical",
  },
  {
    id: 2,
    station: "Station B-05",
    type: "drop",
    value: "-85% vs Avg",
    desc: "Flow rate dropped significantly despite high queue. Check Pump #2 filter.",
    severity: "medium",
  },
  {
    id: 3,
    station: "Station C-09",
    type: "time",
    value: "45 mins",
    desc: "Avg filling time exceeded threshold. 3 Tankers waiting.",
    severity: "low",
  },
];

const pumpStatus = [
  { id: "P1", status: "active", flow: "120 LPM" },
  { id: "P2", status: "active", flow: "115 LPM" },
  { id: "P3", status: "error", flow: "0 LPM" },
  { id: "P4", status: "idle", flow: "0 LPM" },
];

const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}
  >
    {children}
  </div>
);

const Badge = ({ children, type = "success", }) => {
   const themeMode = useSelector((state) => state.theme.mode);
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const borderThem = themeMode ? "border_them_2" : "border-primary";
  const styles = {
    success: "bg-emerald-100 !text-emerald-700 !border-emerald-200",
    warning: "bg-amber-100 !text-amber-700 !border-amber-200",
    danger: "bg-red-100 !text-red-700 !border-red-200",
    critical: "bg-red-50 !text-red-600 !border-red-100 animate-pulse",
    neutral: "bg-slate-100 !text-slate-600 !border-slate-200",
  };
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full ${textColorClass} text-xs ${borderThem} font-semibold border ${styles[type]}`}
    >
      {children}
    </span>
  );
};

export default function StationPerformanceDashboard() {
  const themeMode = useSelector((state) => state.theme.mode);
  const textColorClass = themeMode ? "dark_text-color" : "text-primary";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
  const borderThem = themeMode ? "border_them_2" : "border-primary";
   const tableHedBg = themeMode ? "table_hed_bg" : "tabel_hed_def_bg";
  const scrollBg = themeMode ? "sidebar_scroll_dark" : "sidebar_scroll";

  const [timeRange, setTimeRange] = useState("today");

  const getKpiData = () => {
    if (timeRange === "today")
      return {
        water: "320 KL",
        tankers: "45",
        revenue: "₹ 19,200",
        fillTime: "12m",
      };
    if (timeRange === "mtd")
      return {
        water: "8,500 KL",
        tankers: "1,200",
        revenue: "₹ 5.1L",
        fillTime: "14m",
      };
    return {
      water: "45,000 KL",
      tankers: "6,500",
      revenue: "₹ 27L",
      fillTime: "13m",
    };
  };

  const kpi = getKpiData();

  return (
    <div className={`w-full ${bgColorClass} h-full`}>
      <div
        className={`py-4 md:px-12 px-6 border-b ${borderThem} flex justify-between items-center h-[57px]`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColorClass} items-center  gap-x-2`}
        >
          <p>
            <FaArtstation className="cursor-pointer font-semibold" />
          </p>
          <p className="font-semibold">Station Performance</p>
        </div>
        <div className={`flex ${tableHedBg} border ${borderThem} rounded-lg p-1`}>
          {["today", "mtd", "ytd"].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-1.5 text-sm font-bold cursor-pointer rounded-md uppercase transition-all ${
                timeRange === range
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>
      <div className=" h-[calc(100vh-50px)] pb-10 overflow-y-auto ">
        <div className="px-3 md:px-5 lg:px-12 md:mt-5 mt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card
              className={`p-5 border-l-4 !border-l-blue-500 ${bgColorClass} ${borderThem} relative overflow-hidden`}
            >
              <div className="absolute right-0 top-0 p-4 opacity-10">
                <Droplets className="w-16 h-16 text-blue-600" />
              </div>
              <div className="relative z-10">
                <p
                  className={`text-xs font-bold ${textColorClass} uppercase tracking-wider`}
                >
                  Water Dispensed
                </p>
                <h3 className={`text-3xl font-bold ${textColorClass} mt-1`}>
                  {kpi.water}
                </h3>
                <div className="mt-2 text-xs flex items-center text-emerald-600 font-medium bg-emerald-50 w-max px-2 py-0.5 rounded">
                  <TrendingUp className="w-3 h-3 mr-1" /> +12% vs avg
                </div>
              </div>
            </Card>

            <Card
              className={`p-5 border-l-4 !border-l-emerald-500 ${bgColorClass} ${borderThem} relative overflow-hidden`}
            >
              <div className="absolute right-0 top-0 p-4 opacity-10">
                <IndianRupee className="w-16 h-16 text-emerald-600" />
              </div>
              <div className="relative z-10">
                <p
                  className={`text-xs font-bold ${textColorClass} uppercase tracking-wider`}
                >
                  Revenue (RFID)
                </p>
                <h3 className={`text-3xl font-bold ${textColorClass} mt-1`}>
                  {kpi.revenue}
                </h3>
                <div className="mt-2 text-xs flex items-center text-slate-500">
                  <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-500" />{" "}
                  100% Verified
                </div>
              </div>
            </Card>

               <Card
              className={`p-5 border-l-4 !border-l-amber-500 ${bgColorClass} ${borderThem} relative overflow-hidden`}
            >
              <div className="flex justify-between items-start mb-2">
                <p className={`text-xs font-bold ${textColorClass} uppercase tracking-wider`}>
                  Avg Fill Time
                </p>
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex items-baseline gap-2">
                <h3 className={`text-3xl font-bold ${textColorClass}`}>
                  {kpi.fillTime}
                </h3>
                <span className={`text-xs text-slate-500`}>/ tanker</span>
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                  <span>Efficiency</span>
                  <span>85%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-amber-500 h-1.5 rounded-full"
                    style={{ width: "85%" }}
                  ></div>
                </div>
              </div>
            </Card>

                <Card
              className={`p-5 border-l-4 !border-l-indigo-500 ${bgColorClass} ${borderThem} relative overflow-hidden`}
            >
              <div className="flex justify-between items-start mb-2">
                <p className={`text-xs font-bold ${textColorClass} uppercase tracking-wider`}>
                  System Uptime
                </p>
                <Activity className="w-5 h-5 text-indigo-500" />
              </div>
              <h3 className={`text-3xl font-bold ${textColorClass}`}>98.5%</h3>
              <div className="mt-3 flex gap-0.5">
                {[...Array(12)].map((_, i) => (
                  <div
                    key={i}
                    className={`h-2 flex-1 rounded-sm ${
                      i === 8 ? "bg-red-400" : "bg-emerald-400"
                    }`}
                    title={i === 8 ? "Downtime recorded" : "Active"}
                  ></div>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-1 text-right">
                Last 12 Hours
              </p>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className={`p-3 ${bgColorClass} ${borderThem}`}>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className={`text-lg font-bold ${textColorClass}`}>
                      Hourly Dispensing Performance
                    </h3>
                    <p className={`text-sm ${textColorClass}`}>
                      Detecting sudden spikes or drops against historical
                      patterns
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-xs bg-slate-50 px-3 py-1 rounded-lg border border-slate-100">
                    <span className="flex items-center">
                      <div className="w-3 h-3 bg-blue-500/20 border border-blue-500 mr-2 rounded-sm"></div>{" "}
                      Avg Baseline
                    </span>
                    <span className="flex items-center">
                      <div className="w-3 h-3 bg-blue-600 mr-2 rounded-full"></div>{" "}
                      Today Actual
                    </span>
                  </div>
                </div>

                <div className="h-[320px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={hourlyData}>
                      <defs>
                        <linearGradient
                          id="colorAvg"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#64748b"
                            stopOpacity={0.1}
                          />
                          <stop
                            offset="95%"
                            stopColor="#64748b"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f1f5f9"
                      />
                      <XAxis
                        dataKey="time"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#64748b", fontSize: 12 }}
                        dy={10}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#64748b", fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: "none",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        }}
                        cursor={{ fill: "#f8fafc" }}
                      />
                      <Area
                        type="monotone"
                        dataKey="avg"
                        fill="url(#colorAvg)"
                        stroke="#94a3b8"
                        strokeDasharray="5 5"
                        name="Historical Avg"
                      />
                      <Bar
                        dataKey="actual"
                        barSize={30}
                        radius={[4, 4, 0, 0]}
                        name="Today Actual"
                      >
                        {hourlyData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              entry.status === "Spike" ? "#ef4444" : "#2563eb"
                            }
                          />
                        ))}
                      </Bar>
                      <Line
                        type="monotone"
                        dataKey="actual"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 6 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className={`p-3 flex flex-col justify-between ${bgColorClass} ${borderThem}`}>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className={`text-sm font-bold ${textColorClass}`}>
                      Filling Speed Distribution
                    </h4>
                    <Badge type="warning">Slow: 15%</Badge>
                  </div>
                  <div className="h-[140px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[
                          { range: "Fast (<10m)", count: 20 },
                          { range: "Avg (10-20m)", count: 15 },
                          { range: "Slow (>20m)", count: 5 },
                        ]}
                      >
                        <XAxis
                          dataKey="range"
                          tick={{ fontSize: 10 }}
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                        />
                        <Tooltip cursor={{ fill: "transparent" }} />
                        <Bar
                          dataKey="count"
                          fill="#6366f1"
                          radius={[4, 4, 0, 0]}
                          barSize={40}
                        >
                          {/* Color code bars */}
                          <Cell fill="#10b981" />
                          <Cell fill="#3b82f6" />
                          <Cell fill="#f59e0b" />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

              <Card className={`p-3 justify-between ${bgColorClass} ${borderThem}`}>
                  <div className="flex justify-between items-center mb-4">
                    <h4 className={`text-sm font-bold ${textColorClass} flex items-center gap-2`}>
                      <Fuel className="w-4 h-4 text-slate-500" /> Pump Health
                    </h4>
                    <span className="text-[10px] text-slate-400">
                      Updated: Live
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {pumpStatus.map((pump) => (
                      <div
                        key={pump.id}
                        className={`p-2 rounded border flex flex-col items-center justify-center
                          ${
                            pump.status === "error"
                              ? "bg-red-50 border-red-200"
                              : pump.status === "idle"
                              ? "bg-slate-50 border-slate-200"
                              : "bg-green-50 border-green-200"
                          }
                       `}
                      >
                        <span className="text-xs font-bold text-slate-700">
                          Pump {pump.id.replace("P", "")}
                        </span>
                        {pump.status === "error" ? (
                          <AlertOctagon className="w-5 h-5 text-red-500 my-1" />
                        ) : (
                          <Zap
                            className={`w-5 h-5 my-1 ${
                              pump.status === "active"
                                ? "text-green-500 fill-green-500"
                                : "text-slate-300"
                            }`}
                          />
                        )}
                        <span className="text-[10px] text-slate-500 font-mono">
                          {pump.flow}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <Card className={`flex-1 ${bgColorClass} ${borderThem} border-red-200 flex flex-col shadow-md`}>
                <div className="p-4 border-b border-red-100 bg-red-50 rounded-t-xl flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-red-900 flex items-center gap-2">
                      <AlertOctagon className="w-5 h-5 text-red-600" />
                      Critical Anomalies
                    </h3>
                    <p className="text-xs text-red-700 mt-1">
                      3 issues detected today
                    </p>
                  </div>
                  <div className="w-2 h-2 bg-red-600 rounded-full animate-ping"></div>
                </div>

                <div className="p-4 space-y-4 overflow-y-auto">
                  {anomalyInsights.map((item) => (
                    <div
                      key={item.id}
                      className={`p-3 ${bgColorClass} ${borderThem} border rounded-lg shadow-sm group hover:border-red-200 hover:shadow-md transition-all`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className={`font-bold ${textColorClass} text-sm flex items-center gap-2`}>
                          <MapIcon className="w-3 h-3 text-slate-400" />{" "}
                          {item.station}
                        </span>
                        <Badge type={item.severity}>
                          {item.severity.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="mb-2">
                        <span
                          className={`text-lg font-bold ${
                            item.type === "spike"
                              ? "text-red-600"
                              : `${textColorClass}`
                          }`}
                        >
                          {item.value}
                        </span>
                      </div>
                      <p className={`text-xs ${textColorClass} leading-relaxed border-l-2 ${borderThem} pl-2`}>
                        {item.desc}
                      </p>
                      <div className={`mt-3 pt-2 border-t ${borderThem} flex justify-end`}>
                        <button className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1">
                          Investigate <Settings className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className={`p-3 ${bgColorClass}  rounded-b-xl border-t ${borderThem} text-center`}>
                  <button className="text-xs text-slate-500 hover:text-slate-600 font-medium">
                    View Archived Alerts
                  </button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MapIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
